import { array, nonEmptyArray, number, option, ord, predicate, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { compactArray } from '@mews/fp';
import { ProcessedException } from '../../types';

const arePriceExceptionsCurrenciesUnique = flow(
    nonEmptyArray.flatMap(({ minPrice, maxPrice }: ProcessedException) => [
        minPrice ? minPrice.currency : null,
        maxPrice ? maxPrice.currency : null,
    ]),
    compactArray,
    predicate.or(flow(array.uniq(string.Eq), (list) => list.length === 1))(array.isEmpty)
);

const createValueGetter =
    (exceptions: NonEmptyArray<ProcessedException>) =>
    (method: 'min' | 'max', getComparisonValue: (exception: ProcessedException) => number | null | undefined) =>
        pipe(
            exceptions,
            nonEmptyArray[method](
                ord.contramap((exception: ProcessedException) => {
                    const value = getComparisonValue(exception);
                    switch (method) {
                        case 'min':
                            return value ?? Number.POSITIVE_INFINITY;
                        case 'max':
                            return value ?? 0;
                    }
                })(number.Ord)
            )
        );

const reduceToMostRestrictingExceptions = flow(
    option.fromPredicate(arePriceExceptionsCurrenciesUnique),
    option.map((exceptions: NonEmptyArray<ProcessedException>): ProcessedException => {
        const getValue = createValueGetter(exceptions);
        return {
            minAdvance: getValue('max', ({ minAdvance }) => minAdvance).minAdvance,
            maxAdvance: getValue('min', ({ maxAdvance }) => maxAdvance).maxAdvance,
            minLength: getValue('max', ({ minLength }) => minLength).minLength,
            maxLength: getValue('min', ({ maxLength }) => maxLength).maxLength,
            minPrice: getValue('max', ({ minPrice }) => (minPrice ? minPrice.value : null)).minPrice,
            maxPrice: getValue('min', ({ maxPrice }) => (maxPrice ? maxPrice.value : null)).maxPrice,
            minReservationCount: getValue('max', ({ minReservationCount }) => minReservationCount).minReservationCount,
            maxReservationCount: getValue('min', ({ maxReservationCount }) => maxReservationCount).maxReservationCount,
            secondaryMinLength: getValue('max', ({ secondaryMinLength }) => secondaryMinLength).secondaryMinLength,
            secondaryMaxLength: getValue('min', ({ secondaryMaxLength }) => secondaryMaxLength).secondaryMaxLength,
        };
    })
);

export default reduceToMostRestrictingExceptions;
